import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "./cn"

const noticeVariants = cva(
  "flex min-w-0 items-start gap-2 rounded-2xl border px-4 py-3 text-sm [overflow-wrap:anywhere] [&>*]:min-w-0 [&_svg]:mt-0.5 [&_svg]:size-4 [&_svg]:shrink-0 avex:rounded-avex-md",
  {
    variants: {
      tone: {
        neutral: "border-gray-100 bg-gray-50 text-gray-700 avex:border-line avex:bg-surface-sunken avex:text-ink-secondary",
        success: "border-emerald-100 bg-emerald-50 text-emerald-800 avex:border-transparent avex:bg-success-soft avex:text-success",
        warning: "border-amber-100 bg-amber-50 text-amber-800 avex:border-transparent avex:bg-warning-soft avex:text-warning",
        danger: "border-red-100 bg-red-50 text-red-800 avex:border-transparent avex:bg-danger-soft avex:text-danger",
        info: "border-blue-100 bg-blue-50 text-blue-800 avex:border-transparent avex:bg-info-soft avex:text-info"
      }
    },
    defaultVariants: { tone: "neutral" }
  }
)

/**
 * A message on the page: the result of an action, a warning, an error. The tone
 * carries the meaning together with the words, never colour alone — the same
 * rule as `Badge`. A danger notice is announced at once (`role="alert"`), the
 * others politely (`role="status"`). A long word, path or id wraps inside the
 * box rather than running out of it.
 */
function Notice({ className, tone, role, ...props }: React.ComponentProps<"div"> & VariantProps<typeof noticeVariants>) {
  return (
    <div
      data-slot="notice"
      role={role ?? (tone === "danger" ? "alert" : "status")}
      className={cn(noticeVariants({ tone }), className)}
      {...props}
    />
  )
}

export { Notice, noticeVariants }
